// ── Types ──────────────────────────────────────────────────────────────────────

export interface GeneratedTestStep {
  stepType: 'api' | 'hosted' | 'webhook_wait' | 'manual'
  action: string
  endpoint?: string               // "POST /v2/individuals" — required for api + hosted
  payload?: Record<string, unknown>
  assertion?: string              // "status 200", "entityId present", "workflowStatus == COMPLETE"
}

export interface FrankieCredentials {
  customerId: string
  apiKey: string
  apiBase: string
  machineBase: string
  childCustomerId?: string
}

export interface StepRunResult {
  status: 'pass' | 'fail' | 'waiting' | 'error'
  statusCode?: number
  responseBody?: unknown
  errorMessage?: string
  hostedUrl?: string
  webhookEvent?: unknown
}

export interface CaseRunState {
  status: 'idle' | 'running' | 'pass' | 'fail' | 'error'
  stepStatuses: (StepRunResult | undefined)[]
  activeStepIdx: number | null
  entityId: string | null
}

// ── Credential loading ─────────────────────────────────────────────────────────

const CREDS_KEY = 'ft-credentials-dealhub'

export function loadCredentials(): FrankieCredentials | null {
  try {
    const raw = localStorage.getItem(CREDS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<FrankieCredentials>
    if (!parsed.customerId || !parsed.apiKey) return null
    return {
      customerId: parsed.customerId,
      apiKey: parsed.apiKey,
      apiBase: parsed.apiBase ?? 'https://api.uat.frankie.one/v2',
      machineBase: parsed.machineBase ?? 'https://backend.kycaml.uat.frankiefinancial.io',
      childCustomerId: parsed.childCustomerId,
    }
  } catch {
    return null
  }
}

// ── Entity ID extraction ───────────────────────────────────────────────────────

export function extractEntityId(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null
  const b = body as Record<string, unknown>
  if (typeof b.entityId === 'string') return b.entityId
  const entity = b.entity
  if (entity && typeof entity === 'object') {
    const id = (entity as Record<string, unknown>).entityId
    if (typeof id === 'string') return id
  }
  const data = b.data
  if (data && typeof data === 'object') {
    const id = (data as Record<string, unknown>).entityId
    if (typeof id === 'string') return id
  }
  return null
}

// ── Assertion checking ─────────────────────────────────────────────────────────

function getNestedValue(obj: unknown, keyPath: string): unknown {
  const keys = keyPath.split('.')
  let current: unknown = obj
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

export function checkAssertion(
  assertion: string,
  status: number,
  body: unknown,
): { pass: boolean; message: string } {
  const checks = assertion.split(',').map(s => s.trim()).filter(Boolean)
  for (const check of checks) {
    // "status 200"
    const statusMatch = check.match(/^status\s+(\d+)$/)
    if (statusMatch) {
      const expected = parseInt(statusMatch[1], 10)
      if (status !== expected) {
        return { pass: false, message: `Expected status ${expected}, got ${status}` }
      }
      continue
    }
    // "entityId present"
    const presentMatch = check.match(/^([\w.]+)\s+present$/)
    if (presentMatch) {
      const val = getNestedValue(body, presentMatch[1])
      if (val == null) {
        return { pass: false, message: `Expected ${presentMatch[1]} to be present` }
      }
      continue
    }
    // "workflowStatus == COMPLETE"
    const eqMatch = check.match(/^([\w.]+)\s+==\s+(.+)$/)
    if (eqMatch) {
      const val = getNestedValue(body, eqMatch[1])
      if (String(val) !== eqMatch[2].trim()) {
        return { pass: false, message: `Expected ${eqMatch[1]} == ${eqMatch[2].trim()}, got ${String(val)}` }
      }
      continue
    }
  }
  return { pass: true, message: 'All assertions passed' }
}

// ── Step execution ─────────────────────────────────────────────────────────────

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function proxyHeaders(creds: FrankieCredentials): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-FT-Customer-ID': creds.customerId,
    'X-FT-API-Key': creds.apiKey,
    'X-FT-API-Base': creds.apiBase,
    'X-FT-Machine-Base': creds.machineBase,
    ...(creds.childCustomerId ? { 'X-FT-Child-Customer-ID': creds.childCustomerId } : {}),
  }
}

async function callProxy(
  endpoint: string,
  payload: Record<string, unknown> | undefined,
  creds: FrankieCredentials,
  signal: AbortSignal,
): Promise<{ status: number; body: unknown }> {
  const spaceIdx = endpoint.indexOf(' ')
  const method = spaceIdx === -1 ? 'GET' : endpoint.slice(0, spaceIdx)
  const rawPath = spaceIdx === -1 ? endpoint : endpoint.slice(spaceIdx + 1)
  // Strip /v2 prefix — the proxy's apiBase already includes /v2
  const path = rawPath.replace(/^\/v2(?=\/)/, '')
  const res = await fetch(`/api/frankie-proxy${path}`, {
    method,
    headers: proxyHeaders(creds),
    body: payload ? JSON.stringify(payload) : undefined,
    signal,
  })
  const body = await res.json().catch(() => null)
  return { status: res.status, body }
}

interface WebhookEventRecord {
  id: number
  entityId: string | null
  eventType: string | null
  payload: unknown
  receivedAt: string
}

async function pollForWebhook(
  customerId: string,
  entityId: string | null,
  signal: AbortSignal,
  onEvent: (event: WebhookEventRecord) => void,
): Promise<StepRunResult> {
  const POLL_INTERVAL = 3000
  const TIMEOUT = 120_000
  const startTime = Date.now()

  while (Date.now() - startTime < TIMEOUT) {
    if (signal.aborted) return { status: 'error', errorMessage: 'Aborted' }
    await sleep(POLL_INTERVAL)
    if (signal.aborted) return { status: 'error', errorMessage: 'Aborted' }

    try {
      const res = await fetch(`/api/webhook/frankieone/events?customerId=${encodeURIComponent(customerId)}`, { signal })
      const data = await res.json()
      // frankieWebhook route returns a bare array via jsonResponse(events)
      const events: WebhookEventRecord[] = Array.isArray(data) ? data : ((data as Record<string, unknown>).events as WebhookEventRecord[] | undefined) ?? []
      const match = events.find(e => entityId ? e.entityId === entityId : true)
      if (match) {
        onEvent(match)
        return { status: 'pass', webhookEvent: match }
      }
    } catch {
      // network hiccup — keep polling
    }
  }
  return { status: 'fail', errorMessage: 'Webhook timeout after 2 minutes' }
}

export async function executeStep(
  step: GeneratedTestStep,
  entityId: string | null,
  creds: FrankieCredentials,
  onWebhookEvent: (event: unknown) => void,
  abortSignal: AbortSignal,
): Promise<StepRunResult> {
  const effectiveType = (['api', 'hosted', 'webhook_wait', 'manual'] as const).includes(
    step.stepType as 'api' | 'hosted' | 'webhook_wait' | 'manual'
  ) ? step.stepType : 'manual'

  if (effectiveType === 'manual') {
    return { status: 'waiting' }
  }

  if (effectiveType === 'api') {
    if (!step.endpoint) return { status: 'waiting' }
    try {
      const { status, body } = await callProxy(step.endpoint, step.payload, creds, abortSignal)
      if (step.assertion) {
        const check = checkAssertion(step.assertion, status, body)
        if (!check.pass) {
          return { status: 'fail', statusCode: status, responseBody: body, errorMessage: check.message }
        }
      }
      return { status: 'pass', statusCode: status, responseBody: body }
    } catch (e) {
      const errorName = (e as { name?: string })?.name
      if (errorName === 'AbortError') {
        return { status: 'error', errorMessage: 'Aborted' }
      }
      return { status: 'error', errorMessage: e instanceof Error ? e.message : 'Network error' }
    }
  }

  if (effectiveType === 'hosted') {
    if (!step.endpoint) return { status: 'waiting' }
    try {
      const { status, body } = await callProxy(step.endpoint, step.payload, creds, abortSignal)
      const hostedUrl = (body as Record<string, unknown>)?.url as string | undefined
      return { status: 'waiting', statusCode: status, responseBody: body, hostedUrl }
    } catch (e) {
      const errorName = (e as { name?: string })?.name
      if (errorName === 'AbortError') {
        return { status: 'error', errorMessage: 'Aborted' }
      }
      return { status: 'error', errorMessage: e instanceof Error ? e.message : 'Network error' }
    }
  }

  // webhook_wait
  return pollForWebhook(creds.customerId, entityId, abortSignal, onWebhookEvent)
}
